<template>
  <div
    ref="root"
    class="app-dropdown"
    :class="[`is-${size}`, { 'is-open': open, 'is-block': block, 'is-disabled': disabled }]"
  >
    <button
      ref="trigger"
      type="button"
      class="app-dropdown-trigger"
      :disabled="disabled"
      :aria-haspopup="'listbox'"
      :aria-expanded="open"
      :aria-label="ariaLabel || label || placeholder"
      @click="toggle"
      @keydown.down.prevent="openWith(1)"
      @keydown.up.prevent="openWith(-1)"
    >
      <span class="app-dropdown-copy">
        <small v-if="label">{{ label }}</small>
        <strong :title="selectedLabel">
          <i v-if="selectedIcon" class="fa-solid" :class="selectedIcon"></i>
          {{ selectedLabel }}
        </strong>
      </span>
      <span v-if="selectedMeta" class="app-dropdown-trigger-meta">{{ selectedMeta }}</span>
      <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
    </button>

    <Transition name="favorite-menu">
      <div
        v-if="open"
        ref="menu"
        class="app-dropdown-menu"
        :class="`is-${align}`"
        role="listbox"
        tabindex="-1"
        :aria-activedescendant="activeId"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.home.prevent="moveTo(0)"
        @keydown.end.prevent="moveTo(options.length - 1)"
        @keydown.esc.prevent="close(true)"
        @keydown.enter.prevent="pick(options[highlighted])"
        @keydown.space.prevent="pick(options[highlighted])"
      >
        <button
          v-for="(option, index) in options"
          :id="`${uid}-option-${index}`"
          :key="option.value"
          type="button"
          role="option"
          :aria-selected="option.value === modelValue"
          :disabled="option.disabled"
          :class="{ 'is-active': option.value === modelValue, 'is-highlighted': index === highlighted }"
          @click="pick(option)"
          @mousemove="highlighted = index"
        >
          <span class="app-dropdown-option-copy">
            <span class="app-dropdown-option-label">
              <i v-if="option.icon" class="fa-solid" :class="option.icon"></i>
              {{ option.label }}
            </span>
            <small v-if="option.hint">{{ option.hint }}</small>
          </span>
          <span v-if="option.meta" class="app-dropdown-option-meta">{{ option.meta }}</span>
          <i v-if="option.value === modelValue" class="fa-solid fa-check"></i>
        </button>

        <p v-if="!options.length" class="app-dropdown-empty">{{ emptyLabel }}</p>
      </div>
    </Transition>
  </div>
</template>

<script>
// The application's dropdown.
//
// A native <select> cannot carry a second line, a right hand figure or an icon
// per option, and it is drawn by the operating system, so it ignores the theme.
// This component keeps the look of the rest of the interface and behaves like a
// listbox: arrows move, Enter picks, Escape closes and focus returns to the
// trigger, so it stays usable without a mouse.

let counter = 0

export default {
    name: 'AppDropdown',

    props: {
        modelValue: { type: [String, Number], default: '' },
        // [{ value, label, hint, meta, icon, disabled }]
        options: { type: Array, default: () => [] },
        label: { type: String, default: '' },
        placeholder: { type: String, default: 'Select' },
        emptyLabel: { type: String, default: 'Nothing to choose from' },
        ariaLabel: { type: String, default: '' },
        size: { type: String, default: 'md' },
        align: { type: String, default: 'left' },
        block: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        // Repeats the selected option's figure on the trigger, for the cases
        // where that figure is the reason the option was picked.
        showMeta: { type: Boolean, default: false },
    },

    emits: ['update:modelValue', 'change'],

    data() {
        counter += 1
        return {
            uid: `app-dropdown-${counter}`,
            open: false,
            highlighted: -1,
        }
    },

    computed: {
        selected() {
            return this.options.find((option) => option.value === this.modelValue) || null
        },
        selectedLabel() {
            return this.selected ? this.selected.label : this.placeholder
        },
        selectedIcon() {
            return this.selected ? this.selected.icon : ''
        },
        selectedMeta() {
            return this.selected && this.showMeta ? this.selected.meta : ''
        },
        activeId() {
            return this.highlighted >= 0 ? `${this.uid}-option-${this.highlighted}` : ''
        },
    },

    watch: {
        disabled(value) {
            if (value) this.close()
        },
    },

    mounted() {
        document.addEventListener('click', this.onDocumentClick, true)
        window.addEventListener('resize', this.onViewportChange)
        window.addEventListener('scroll', this.onViewportChange, true)
    },

    beforeUnmount() {
        document.removeEventListener('click', this.onDocumentClick, true)
        window.removeEventListener('resize', this.onViewportChange)
        window.removeEventListener('scroll', this.onViewportChange, true)
    },

    methods: {
        toggle() {
            if (this.open) this.close(true)
            else this.openWith(0)
        },

        openWith(step) {
            if (this.disabled) return
            const current = this.options.findIndex((option) => option.value === this.modelValue)
            this.highlighted = current >= 0 ? current : Math.max(0, step)
            this.open = true
            this.$nextTick(() => {
                if (this.$refs.menu) this.$refs.menu.focus()
                this.scrollHighlightedIntoView()
            })
        },

        close(focusTrigger = false) {
            if (!this.open) return
            this.open = false
            this.highlighted = -1
            if (focusTrigger && this.$refs.trigger) this.$refs.trigger.focus()
        },

        move(step) {
            if (!this.options.length) return
            let next = this.highlighted
            for (let i = 0; i < this.options.length; i += 1) {
                next = (next + step + this.options.length) % this.options.length
                if (!this.options[next].disabled) break
            }
            this.highlighted = next
            this.scrollHighlightedIntoView()
        },

        moveTo(index) {
            if (index < 0 || index >= this.options.length) return
            this.highlighted = index
            this.scrollHighlightedIntoView()
        },

        scrollHighlightedIntoView() {
            const menu = this.$refs.menu
            if (!menu) return
            const item = menu.children[this.highlighted]
            if (item && item.scrollIntoView) item.scrollIntoView({ block: 'nearest' })
        },

        pick(option) {
            if (!option || option.disabled) return
            this.close(true)
            if (option.value === this.modelValue) return
            this.$emit('update:modelValue', option.value)
            this.$emit('change', option.value)
        },

        onDocumentClick(event) {
            if (!this.open) return
            if (this.$refs.root && this.$refs.root.contains(event.target)) return
            this.close()
        },

        // The menu is absolutely positioned next to the trigger, so anything that
        // moves the trigger would leave it hanging in the wrong place.
        onViewportChange(event) {
            if (!this.open) return
            if (event && event.target && this.$refs.menu && this.$refs.menu.contains(event.target)) return
            this.close()
        },
    },
}
</script>
